import axios from "axios";
import { signInWithCustomToken } from "firebase/auth";
import { firebaseAuth } from "../config/firebase";
import { getCookie } from "../utils/cookieUtils";

let pending: Promise<string> | undefined;

export type GatewayTokenRestoreStage =
  | "app_token_validation"
  | "firebase_auth_ready"
  | "gateway_session_exchange"
  | "firebase_custom_token_sign_in"
  | "firebase_id_token";

export class GatewayTokenRestoreError extends Error {
  constructor(
    readonly stage: GatewayTokenRestoreStage,
    readonly httpStatus?: number,
    readonly appTokenAgeSeconds?: number,
    readonly appTokenExpired?: boolean,
  ) {
    super("Could not restore an authenticated API session");
    this.name = "GatewayTokenRestoreError";
  }
}

export const getGatewayIdToken = (): Promise<string> => getGatewayIdTokenFromAppToken(getCookie("authToken"));

export const getGatewayIdTokenFromAppToken = (appToken?: string | null): Promise<string> => {
  if (!pending) {
    pending = (async () => {
      let stage: GatewayTokenRestoreStage = "app_token_validation";
      let appTokenAgeSeconds: number | undefined;
      let appTokenExpired: boolean | undefined;
      try {
        if (!appToken) throw new Error("Authentication required");
        stage = "firebase_auth_ready";
        await firebaseAuth.authStateReady();
        // Compare the session subject only to decide whether restoration is
        // needed. Authentication is performed by the backend and Firebase.
        let subject: string | undefined;
        try {
          const payload = appToken.split(".")[1]!;
          const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
          subject = claims.sub;
          const now = Math.floor(Date.now() / 1000);
          if (typeof claims.iat === "number") appTokenAgeSeconds = Math.max(0, now - claims.iat);
          if (typeof claims.exp === "number") appTokenExpired = claims.exp <= now;
        } catch { /* Invalid cookies are rejected by the session endpoint. */ }
        if (!firebaseAuth.currentUser || firebaseAuth.currentUser.uid !== subject) {
          stage = "gateway_session_exchange";
          const { data } = await axios.post<{ customToken: string }>(
            `${import.meta.env.VITE_AUTH_API}/api/auth/gateway-session`,
            undefined,
            { headers: { Authorization: `Bearer ${appToken}` } },
          );
          stage = "firebase_custom_token_sign_in";
          await signInWithCustomToken(firebaseAuth, data.customToken);
        }
        stage = "firebase_id_token";
        return await firebaseAuth.currentUser!.getIdToken();
      } catch (error) {
        if (error instanceof GatewayTokenRestoreError) throw error;
        const httpStatus = axios.isAxiosError(error) ? error.response?.status : undefined;
        throw new GatewayTokenRestoreError(stage, httpStatus, appTokenAgeSeconds, appTokenExpired);
      }
    })().finally(() => { pending = undefined; });
  }
  return pending;
};
